const parseRosterNames = (value) =>
  value
    .split(/\r?\n/)
    .map((name) => name.trim())
    .filter(Boolean);

class ProductBuilder extends HTMLElement {
  #currentStep = 1;
  #furthestStep = 1;
  #abortController = new AbortController();
  #groupSelectionMade = false;
  #styleSelectionMade = false;
  #state = {
    tripName: '',
    destination: '',
    year: '',
    insideJoke: '',
    neededBy: '',
    rosterMethod: 'later',
    rosterText: '',
    playerNames: [],
    collection: '',
    groupSize: null,
    totalPrice: '',
    perPlayerPrice: '',
  };

  connectedCallback() {
    const product = this.closest('product-component');
    if (!product) return;

    this.product = product;
    const preselectedStyle = product.querySelector('[data-design-style-value]:checked');
    this.#styleSelectionMade =
      preselectedStyle instanceof HTMLInputElement || new URLSearchParams(window.location.search).has('option_values');
    this.actionBar = this.querySelector('[data-builder-nav]');
    this.backButton = this.querySelector('[data-builder-back]');
    this.continueButton = this.querySelector('[data-builder-continue]');
    product.classList.add('product-builder-active');

    const { signal } = this.#abortController;
    this.querySelector('[data-builder-continue]')?.addEventListener('click', this.#continue, { signal });
    this.querySelector('[data-builder-back]')?.addEventListener('click', this.#back, { signal });
    this.querySelectorAll('[data-builder-go-to]').forEach((button) => {
      button.addEventListener('click', this.#goToFromProgress, { signal });
    });
    product.addEventListener('change', this.#handleProductChange, { signal });
    product.addEventListener('input', this.#handleProductInput, { signal });
    product.addEventListener('click', this.#handleReviewEdit, { signal });
    product.addEventListener('click', this.#handleGroupSelection, { signal });
    document.addEventListener('shopify:block:select', this.#handleThemeEditorSelection, { signal });

    this.#updateSelectedTotal();
    this.#syncState();
    this.#showStep(1, false);
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
    this.product?.classList.remove('product-builder-active');
    this.product?.querySelectorAll('[data-builder-step]').forEach((panel) => panel.removeAttribute('hidden'));
    this.actionBar?.remove();
  }

  #continue = () => {
    if (!this.#validateStep(this.#currentStep)) return;
    this.#syncState();
    this.#furthestStep = Math.min(4, Math.max(this.#furthestStep, this.#currentStep + 1));
    this.#showStep(Math.min(4, this.#currentStep + 1));
  };

  #back = () => {
    this.#showStep(Math.max(1, this.#currentStep - 1));
  };

  #goToFromProgress = (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;
    const step = Number.parseInt(button.dataset.builderGoTo ?? '', 10);
    if (!Number.isInteger(step) || step > this.#furthestStep) return;
    this.#showStep(step);
  };

  #handleThemeEditorSelection = (event) => {
    if (!(event.target instanceof Element) || !this.product?.contains(event.target)) return;
    const panel = event.target.closest('[data-builder-step]') ?? event.target.querySelector('[data-builder-step]');
    if (!(panel instanceof HTMLElement)) return;
    const step = Number.parseInt(panel.dataset.builderStep ?? '', 10);
    if (!Number.isInteger(step)) return;
    this.#furthestStep = Math.max(this.#furthestStep, step);
    this.#showStep(step, false);
    event.target.closest('.optional-trip-details')?.setAttribute('open', '');
  };

  #handleProductChange = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.closest('.variant-option--player-count')) {
      this.#groupSelectionMade = true;
    }
    if (event.target instanceof HTMLInputElement && event.target.dataset.optionName === 'Design Style') {
      this.#styleSelectionMade = true;
    }
    queueMicrotask(() => {
      this.#updateSelectedTotal();
      this.#syncState();
      this.#updateActionState();
    });
  };

  #handleProductInput = () => {
    this.#syncState();
    this.#updateActionState();
  };

  #handleReviewEdit = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-builder-edit-step]') : null;
    if (!(button instanceof HTMLButtonElement)) return;
    const step = Number.parseInt(button.dataset.builderEditStep ?? '', 10);
    if (!Number.isInteger(step) || step < 1 || step >= 4 || step > this.#furthestStep) return;
    this.#showStep(step);
  };

  #handleGroupSelection = (event) => {
    const style = event.target instanceof Element ? event.target.closest('[data-design-style-value]') : null;
    if (style instanceof HTMLInputElement) this.#styleSelectionMade = true;
    const option = event.target instanceof Element
      ? event.target.closest('.variant-option--player-count .variant-option__button-label')
      : null;
    if (!(option instanceof HTMLElement)) return;
    this.#groupSelectionMade = true;
    queueMicrotask(() => {
      this.#updateSelectedTotal();
      this.#syncState();
      this.#updateActionState();
    });
  };

  #updateSelectedTotal() {
    const label = this.actionBar?.querySelector('[data-builder-total-label]');
    const desktopLabel = this.actionBar?.querySelector('[data-builder-total-label-desktop]');
    const total = this.actionBar?.querySelector('[data-builder-total]');
    const totalContainer = total?.closest('[data-builder-starting-price]');
    const selectedPack = this.product?.querySelector('.variant-option--player-count input:checked');
    const selectedCard = selectedPack?.closest('.variant-option__button-label');
    const selectedPrice = selectedCard?.querySelector('[data-option-total-price]')?.textContent?.trim();
    const selectedCount = selectedPack instanceof HTMLInputElement
      ? selectedPack.value.match(/^(4|8|12)\b/i)?.[1]
      : null;

    if (this.#groupSelectionMade && selectedPrice && selectedCount) {
      if (label instanceof HTMLElement) label.textContent = `${selectedCount}-player set ·`;
      if (desktopLabel instanceof HTMLElement) {
        let desktopLabelText = `${selectedCount}-player set ·`;

        if (this.#currentStep === 2) desktopLabelText = 'Selected total';
        if (this.#currentStep === 3) desktopLabelText = 'Your set';

        desktopLabel.textContent = desktopLabelText;
      }
      if (total instanceof HTMLElement) total.textContent = `${selectedPrice} total`;
      return;
    }

    if (label instanceof HTMLElement) label.textContent = '4-PLAYER SET FROM';
    if (desktopLabel instanceof HTMLElement) desktopLabel.textContent = '4-PLAYER SET FROM';
    if (total instanceof HTMLElement && totalContainer instanceof HTMLElement) {
      total.textContent = totalContainer.dataset.builderStartingPrice ?? '';
    }
  }

  #propertyValue(key) {
    const field = this.product?.querySelector(
      `[data-property-key="${key}"] input, [data-property-key="${key}"] textarea, [data-property-key="${key}"] select`
    );
    return field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement
      ? field.value.trim()
      : '';
  }

  #syncState() {
    if (!this.product) return;

    const selectedPack = this.product.querySelector('.variant-option--player-count input:checked');
    const packValue = selectedPack instanceof HTMLInputElement ? selectedPack.value : '';
    const groupMatch = packValue.match(/^(4|8|12)\b/i);
    const selectedCard = selectedPack?.closest('.variant-option__button-label');
    const selectedCollection = Array.from(
      this.product.querySelectorAll('variant-picker input[data-option-name]:checked')
    ).find(
      (option) =>
        option instanceof HTMLInputElement && option.dataset.optionName?.trim().toLowerCase() === 'design style'
    );
    const rosterChoice = this.product.querySelector('[data-roster-option]:checked');
    const rosterMethod = rosterChoice instanceof HTMLInputElement ? rosterChoice.dataset.rosterOption : 'later';
    const rosterField = this.product.querySelector('[data-player-names-source]');
    const rosterText = rosterField instanceof HTMLTextAreaElement ? rosterField.value : '';

    this.#state = {
      tripName: this.#propertyValue('trip-or-event-name'),
      destination: this.#propertyValue('destination-or-course'),
      year: this.#propertyValue('year'),
      insideJoke: this.#propertyValue('inside-joke-or-custom-note'),
      neededBy: this.#propertyValue('needed-by-date'),
      rosterMethod: rosterMethod === 'now' ? 'now' : 'later',
      rosterText,
      playerNames: parseRosterNames(rosterText),
      collection: selectedCollection instanceof HTMLInputElement ? selectedCollection.value : '',
      groupSize: groupMatch ? Number.parseInt(groupMatch[1], 10) : null,
      totalPrice: selectedCard?.querySelector('[data-option-total-price]')?.textContent?.trim() ?? '',
      perPlayerPrice:
        selectedCard?.querySelector('.variant-option__per-player-price')?.textContent?.trim().replace(/\s*per player\s*$/i, '') ?? '',
    };

    this.#renderReview();
  }

  #renderReview() {
    const summary = this.product?.querySelector('[data-purchase-summary]');
    if (!(summary instanceof HTMLElement)) return;

    const setText = (selector, value) => {
      const target = summary.querySelector(selector);
      if (target) target.textContent = value?.trim() || 'Not entered';
    };

    setText('[data-summary-style]', this.#state.collection);
    setText('[data-summary-trip-name]', this.#state.tripName);
    setText('[data-summary-destination]', this.#state.destination);
    setText('[data-summary-year]', this.#state.year);

    const groupSize = this.#state.groupSize;
    const playerCount = summary.querySelector('[data-summary-player-count]');
    const setLabel = summary.querySelector('[data-summary-set-label]');
    if (playerCount && groupSize) playerCount.textContent = groupSize.toString();
    if (setLabel && groupSize) setLabel.textContent = `${groupSize}-player personalized set`;

    const deferred = this.#state.rosterMethod === 'later';
    const rosterMethod = summary.querySelector('[data-summary-roster-method]');
    const namesRow = summary.querySelector('[data-summary-names-row]');
    const namesCount = summary.querySelector('[data-summary-names-count]');
    const namesLabel = summary.querySelector('[data-summary-names-label]');
    const names = summary.querySelector('[data-summary-player-names]');
    if (rosterMethod instanceof HTMLElement) {
      rosterMethod.textContent = 'Roster will be submitted after checkout.';
      rosterMethod.hidden = !deferred;
    }
    if (namesRow instanceof HTMLElement) namesRow.hidden = deferred;
    if (namesCount) namesCount.textContent = this.#state.playerNames.length.toString();
    if (namesLabel) namesLabel.textContent = this.#state.playerNames.length === 1 ? 'name provided' : 'names provided';
    if (names) names.textContent = this.#state.playerNames.join(' · ');

    const total = summary.querySelector('[data-summary-total]');
    const perPlayer = summary.querySelector('[data-summary-per-player]');
    const pricePerPlayer = summary.querySelector('[data-summary-price-per-player]');
    if (total && this.#state.totalPrice) total.textContent = this.#state.totalPrice;
    if (perPlayer && this.#state.perPlayerPrice) perPlayer.textContent = this.#state.perPlayerPrice;
    if (pricePerPlayer && this.#state.perPlayerPrice) pricePerPlayer.textContent = this.#state.perPlayerPrice;
  }

  #updateActionState() {
    if (!(this.continueButton instanceof HTMLButtonElement)) return;
    const unavailable =
      (this.#currentStep === 1 && !this.#styleSelectionMade) ||
      (this.#currentStep === 2 && !this.#groupSelectionMade);
    this.continueButton.disabled = Boolean(unavailable);
  }

  #moveActionBar(step) {
    if (!(this.actionBar instanceof HTMLElement) || !this.product || step === 4) return;
    const panel =
      step === 1
        ? this.product.querySelector('[data-design-style-selector]')?.closest('[data-builder-step="1"]')
        : step === 2
          ? this.product.querySelector('.product-builder__size-step')
          : this.product.querySelector('.product-builder__step-three');
    if (panel instanceof HTMLElement) panel.insertAdjacentElement('afterend', this.actionBar);
  }

  #validateStep(step) {
    if (!this.product) return true;

    if (step === 1) {
      if (this.#styleSelectionMade) return true;
      this.product.querySelector('[data-design-style-selector]')?.scrollIntoView({ behavior: this.#scrollBehavior(), block: 'center' });
      return false;
    }

    if (step === 2) {
      return this.#groupSelectionMade;
    }

    if (step !== 3) return true;

    const fields = this.product.querySelectorAll(
      '[data-builder-step="3"] input:not([type="radio"]):not([type="checkbox"]):not([data-player-names-source]), [data-builder-step="3"] textarea:not([data-player-names-source]), [data-builder-step="3"] select'
    );

    for (const field of fields) {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement)) continue;
      if (field.disabled || field.closest('[hidden]')) continue;
      if (field.checkValidity()) continue;
      field.reportValidity();
      field.focus({ preventScroll: true });
      field.scrollIntoView({ behavior: this.#scrollBehavior(), block: 'center' });
      return false;
    }

    const rosterChoice = this.product.querySelector('[data-roster-option]:checked');
    if (!(rosterChoice instanceof HTMLInputElement)) {
      this.product.querySelector('[data-roster-choice]')?.scrollIntoView({ behavior: this.#scrollBehavior(), block: 'center' });
      return false;
    }

    const rosterComponent = this.product.querySelector('product-custom-property-component:has([data-pack-validation])');
    if (rosterComponent && typeof rosterComponent.validateRoster === 'function' && !rosterComponent.validateRoster(true)) {
      return false;
    }

    return true;
  }

  #showStep(step, shouldScroll = true) {
    if (!this.product) return;
    this.#syncState();
    this.#currentStep = step;
    this.product.dataset.builderCurrentStep = step.toString();
    this.#updateSelectedTotal();

    this.product.querySelectorAll('[data-builder-step]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      const isCurrent = panel.dataset.builderStep === step.toString();
      panel.hidden = !isCurrent;
      panel.setAttribute('aria-hidden', (!isCurrent).toString());
    });

    this.querySelectorAll('[data-progress-step]').forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      const itemStep = Number.parseInt(item.dataset.progressStep ?? '', 10);
      item.toggleAttribute('data-complete', itemStep < step);
      const button = item.querySelector('button');
      if (button instanceof HTMLButtonElement) {
        button.disabled = itemStep > this.#furthestStep;
        if (itemStep === step) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
      }
    });

    const number = this.querySelector('[data-builder-step-number]');
    if (number) number.textContent = step.toString();

    const back = this.backButton;
    const continueButton = this.continueButton;
    const nav = this.actionBar;
    if (back instanceof HTMLButtonElement) back.hidden = step === 1;
    if (continueButton instanceof HTMLButtonElement) {
      continueButton.hidden = step === 4;
      continueButton.textContent =
        step === 1 ? 'Continue to Group Size' : step === 2 ? 'Continue to Personalization' : 'Review My Set';
    }
    if (nav instanceof HTMLElement) nav.hidden = step === 4;
    this.#moveActionBar(step);
    this.#updateActionState();

    if (shouldScroll) {
      this.scrollIntoView({ behavior: this.#scrollBehavior(), block: 'start' });
    }
  }

  #scrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
  }
}

if (!customElements.get('product-builder')) {
  customElements.define('product-builder', ProductBuilder);
}
